import axios from 'axios';
import { AppError } from '../errors/AppError';
import { HttpError } from '../errors/HttpError';
import { InvalidSessionError } from '../errors/InvalidSessionError';
import { ProbeService } from './ProbeService';
import { SessionService } from './SessionService';

/**
 * Create HTTP Requests.
 */
export class ApiService {
  /**
   * Token of the request.
   *
   * @type {string}
   */
  static token;

  /**
   * Session Id.
   *
   * @type {string}
   */
  static sessionId;

  /**
   * Configuration of the service.
   *
   * @type {object}
   */
  static config = { baseUrl: '', timeout: 200 };

  /**
   * Boolean if a request is pending.
   *
   * @type {boolean}
   */
  static requestPending = false;

  /**
   * List of http code which don't need to retry.
   *
   * @type {Array}
   */
  // 400 Bad Request
  // 401 Unauthorized
  // 403 Forbidden
  // 405 Method not Allowed
  // 406 Not Acceptable
  // 413 Payload Too Large
  // 414 Uri Too Long
  // 415 Unsupported Media Type
  static noRetryHttpCodes = [302, 400, 401, 403, 405, 406, 413, 414, 415];

  /**
   * List of step to skip the confirmView,.
   */
  static skipConfirm = ['invitationtv', 'invitationprocuration'];

  /**
   * List of redirect status.
   */
  static redirectStatus = [301, 302];

  /**
   * List of http code in order to display the session error.
   *
   * @type {Array}
   */
  static sessionError = [401002, 401003];

  /**
   * Api Schema to interrogate.
   *
   * @type {Array}
   */
  static apiSchema = {};

  /**
   * Init the configuration of the Api Service.
   *
   * @param {object} namespace - The namespace data to init the service.
   */
  static init(namespace) {
    ApiService.config = namespace.config;
    ApiService.credentials = namespace.credentials;
    ApiService.token = namespace.token;
    ApiService.sessionId = namespace.sessionId;
    ApiService.service = namespace.service;
    ApiService.polling = namespace.polling;
    ApiService.apiSchema = namespace.apiSchema;
    ApiService.modaleEnabled = namespace.modaleEnabled;
    ApiService.invitationId = namespace.invitationId;
    SessionService.initConfig(namespace.session);
  }

  /**
   *  Method to know if we should skip the confirm step.
   *
   *  @returns {boolean} Should we skip the confirm step ?
   */
  static shouldSkipConfirm() {
    return ApiService.skipConfirm.includes(ApiService.service);
  }

  /**
   * Get the config of the Api Service.
   *
   * @returns {object} The configuration of the service.
   */
  static getConfig() {
    return ApiService.config;
  }

  /**
   * Get the session if of the Api Service.
   *
   * @returns {string} The session id of the service.
   */
  static getSessionId() {
    return ApiService.sessionId;
  }

  /**
   * Get the token of the Api Service.
   *
   * @returns {string} The token of the service.
   */
  static getToken() {
    return ApiService.token;
  }

  /**
   * HandleErrorResponse.
   *
   * @param {object} err - Error request.
   * @param {boolean} silent - Don't display technical error.
   */
  static handleErrorResponse(err, silent) {
    // there is nothing to do here if the error should not be displayed
    // then just return
    if (silent) {
      return;
    }

    let errCode;

    if (err.response && err.response.data) {
      errCode = err.response.data.code;
    }

    let error;

    if (ApiService.sessionError.indexOf(errCode) !== -1) {
      error = new InvalidSessionError(err);
    } else {
      error = new HttpError(err);
    }

    if (ApiService.redirectStatus.indexOf(ApiService.redirectStatus) === -1) {
      // Display the error
      AppError.showError(error);
    }
  }

  /**
   * Set headers from xhr response.
   *
   * @param {object} response - Request response.
   */
  static setHeadersResponse(response) {
    if (response && response.headers['x-xsrf-token'] && response.headers['x-session-id']) {
      ApiService.token = response.headers['x-xsrf-token'];
      ApiService.sessionId = response.headers['x-session-id'];
    }
  }

  /**
   * Send request.
   *
   * @param   {object}  options - Url parameter.
   * @param   {boolean} silent -  - Boolean parameter.
   * @returns {[type]} Promise to send.
   */
  static makeRequest(options, silent) {
    // Init Probe Services
    ProbeService.startTimer();

    // Set Axios Options
    const axiosOption = {
      baseURL: ApiService.config.baseUrl,
      timeout: ApiService.config.timeout,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    };

    if (ApiService.token && ApiService.sessionId) {
      axiosOption.headers['x-xsrf-token'] = ApiService.token;
      axiosOption.headers['x-session-id'] = ApiService.sessionId;
    }

    const httpClient = axios.create(axiosOption);

    let retryCount = 0;

    // Retry Pattern Strategy
    httpClient.interceptors.response.use((response) => {
      ApiService.setHeadersResponse(response);
      ApiService.requestPending = false;

      return Promise.resolve(response);
    }, (err) => {
      // Retry the request
      ApiService.setHeadersResponse(err.response);
      const { config } = err;

      // Update xsrf with last token, if existing
      if (err.response && err.response.headers && err.response.headers['x-xsrf-token']) {
        config.headers['x-xsrf-token'] = err.response.headers['x-xsrf-token'];
      }

      // Check if we've maxed out the total number of retries
      if (retryCount >= config.retry || (err && err.response && ApiService.noRetryHttpCodes.indexOf(err.response.status) !== -1)) {
        ApiService.requestPending = false;

        ApiService.handleErrorResponse(err, silent);

        // Reject with the error
        return Promise.reject(err);
      }

      // Increase the retry count
      retryCount += 1;

      return httpClient(config);
    });

    const opts = {
      url: options.url,
      method: options.method || 'GET',
      data: options.body || {},
      retry: 3,
    };

    ApiService.requestPending = true;

    return httpClient(opts);
  }
}
